import { useEffect, useRef } from "react";
import { z } from "zod";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  IconArrowsDiagonalMinimize2,
  IconSparkles,
  IconX,
} from "@tabler/icons-react";
import { ChatInput } from "../ui/ChatInput";
import { sessionStore, useStore } from "../../lib/store";
import { useDictationControl } from "../../domains/gpt/hooks/useDictationControl";

const quickComposerStateSchema = z.enum(["open", "minimized", "closed"]);

// Per tab and not per device: a composer closed in one window should not
// vanish from another, and a new session starts with it in reach again.
const quickComposerStateStore = sessionStore(
  "desktop-quick-composer-state",
  "open",
  quickComposerStateSchema
);

// The half-typed question survives a trip to an article and back.
const quickComposerDraftStore = sessionStore(
  "desktop-quick-composer-draft",
  "",
  z.string()
);

// How many composers are on screen. The shell's ⌘J reopens one only when one
// is mounted; otherwise the shortcut would write a state nobody draws.
let mountedComposers = 0;

/**
 * ⌘J: bring the composer back, whatever state it was left in. `false` when no
 * page shows a composer, so the caller can do what ⌘J does elsewhere.
 */
function openDesktopQuickComposer(): boolean {
  if (mountedComposers === 0) return false;
  quickComposerStateStore.set("open");
  return true;
}

/**
 * A JuisciGPT composer floating over a desktop page (Figma "composer — pièce
 * détachée"). It only COMPOSES: sending hands the question to `/gpt/chat/new`,
 * which asks it on arrival (`initialQuestion`) in the landing's own mode.
 *
 * No mode picker (Figma 5692:150686): the question goes to Auto, which routes it.
 *
 * No "+" menu: files and references attach to a conversation row, and minting
 * one from a page that is not a conversation is the landing's job.
 */
/**
 * The composer's state and machinery: its stores, the textarea the dictation
 * inserts at, and its registration for the shell's ⌘J.
 */
function useDesktopQuickComposer() {
  const [state, setState] = useStore(quickComposerStateStore);
  const [draft, setDraft] = useStore(quickComposerDraftStore);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const history = useHistory();
  const dictation = useDictationControl({
    onTranscript: setDraft,
    textareaRef: composerRef,
    onSend: () => {
      const question = draft.trim();
      setDraft("");
      history.push("/gpt/chat/new", {
        initialQuestion: question,
        // Every page that shows the composer is an Explore feed.
        askedFrom: {
          kind: "explore",
          path: history.location.pathname + history.location.search,
        },
      });
    },
    canSend: () => draft.trim() !== "",
  });
  useEffect(() => {
    mountedComposers += 1;
    return () => {
      mountedComposers -= 1;
    };
  }, []);
  return {
    state,
    setState,
    draft,
    setDraft,
    composerRef,
    dictation,
  };
}

function DesktopQuickComposer(): React.JSX.Element | null {
  const { t } = useTranslation();
  const { state, setState, draft, setDraft, composerRef, dictation } =
    useDesktopQuickComposer();

  switch (state) {
    case "closed":
      return null;
    case "minimized":
      return (
        <button
          type="button"
          onClick={() => {
            setState("open");
          }}
          className="fixed right-6 bottom-6 z-30 flex cursor-pointer items-center gap-2 rounded-full border border-ds-black/4 bg-ds-slate-50 py-2 pr-4 pl-3 text-ds-slate-900 shadow-floating-composer"
        >
          <IconSparkles size={16} stroke={1.5} />
          <span className="text-footnote font-medium">{t("Quick task")}</span>
          <kbd className="font-dm-sans text-xs text-ds-slate-500">⌘J</kbd>
        </button>
      );
    case "open":
      return (
        <section
          aria-label={t("Quick task")}
          className="fixed right-6 bottom-6 z-30 flex w-109 flex-col gap-2.5 rounded-2xl border border-ds-black/4 bg-ds-slate-50 p-1 shadow-floating-composer"
        >
          <div className="flex items-center gap-2.5 p-2">
            <h2 className="min-w-0 flex-1 truncate text-ui-body font-semibold text-ds-slate-900">
              {t("Quick task")}
            </h2>
            <div className="flex items-center gap-4 text-ds-slate-900">
              <button
                type="button"
                aria-label={t("Minimize")}
                onClick={() => {
                  dictation.stop();
                  setState("minimized");
                }}
                className="flex size-6 cursor-pointer items-center justify-center"
              >
                <IconArrowsDiagonalMinimize2 size={20} stroke={1.5} />
              </button>
              <button
                type="button"
                aria-label={t("Close")}
                onClick={() => {
                  dictation.stop();
                  setState("closed");
                }}
                className="flex size-6 cursor-pointer items-center justify-center"
              >
                <IconX size={20} stroke={1.5} />
              </button>
            </div>
          </div>
          <div className="rounded-xl border border-ds-black/4 bg-ds-white/72">
            <ChatInput
              ref={composerRef}
              variant="ghost"
              className="w-full"
              placeholder={t("Ask JuisciGPT a clinical question…")}
              value={draft}
              readOnly={dictation.composerReadOnly}
              onFocus={() => {
                if (dictation.isActive) dictation.stop();
              }}
              onChange={(e) => {
                setDraft(e.target.value);
              }}
              mode={null}
              leading={null}
              leadingPanel={null}
              banner={null}
              attach={null}
              dictation={dictation.composer}
              stream={null}
              // While dictating, emptiness is not a block: the send waits for
              // the transcript (see `useDictationControl`).
              sendDisabled={dictation.isActive ? false : draft.trim() === ""}
              onSend={dictation.send}
            />
          </div>
        </section>
      );
    default:
      throw new Error(state satisfies never);
  }
}

export { DesktopQuickComposer, openDesktopQuickComposer };
